import {Button, ButtonGroup, closeDialog, openDialog} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {FinancingCompositionScene, FinancialProductType} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CompositionEditor, CompositionEditorApplyResult} from './CompositionEditor';
import {CompositionEditorMode} from './types/CompositionEditorMode';

interface CompositionGraphicSectionProps extends RequiredTestIdProps {
  composition: FinancingCompositionScene | null;
  mode?: CompositionEditorMode;
  financialProductType?: FinancialProductType;
  sourcePhotoIndex?: number | null;
  isDisabled?: boolean;
  onApply: (result: CompositionEditorApplyResult) => void;
  onRemove: () => void;
}

const EDITOR_DIALOG_ID = 'composition-editor';

/**
 * "Create / Edit / Remove graphic" controls of a form holding a composition. The editor runs in a
 * dialog and only hands the applied scene back; nothing is persisted until the form itself is saved.
 */
export function CompositionGraphicSection(props: CompositionGraphicSectionProps) {
  const openEditor = () =>
    openDialog(
      <CompositionEditor
        initialScene={props.composition}
        mode={props.mode}
        financialProductType={props.financialProductType}
        sourcePhotoIndex={props.sourcePhotoIndex}
        onApply={(result) => {
          props.onApply(result);
          closeDialog(EDITOR_DIALOG_ID);
        }}
        onCancel={() => closeDialog(EDITOR_DIALOG_ID)}
        data-testid={suffixTestId('editor', props)}
      />,
      {
        id: EDITOR_DIALOG_ID,
        size: 'xLarge',
        title: i18n.t('entity.watermark.composition.labels.editorTitle'),
        'data-testid': suffixTestId('editorDialog', props),
      }
    );

  return (
    <VStack spacing={2} align="flex-start">
      <Text size="small" color="secondary">
        {isNil(props.composition)
          ? i18n.t('entity.watermark.composition.labels.noGraphicDescription')
          : i18n.t('entity.watermark.composition.labels.graphicAppliedDescription')}
      </Text>
      <Show when={isNil(props.composition)}>
        <Button
          variant="secondary"
          leftIcon="content/add"
          title={i18n.t('entity.watermark.composition.actions.createGraphic')}
          isDisabled={props.isDisabled}
          onClick={openEditor}
          data-testid={suffixTestId('create', props)}
        />
      </Show>
      <Show when={isNotNil(props.composition)}>
        <ButtonGroup>
          <Button
            variant="secondary"
            leftIcon="image/edit"
            title={i18n.t('entity.watermark.composition.actions.editGraphic')}
            isDisabled={props.isDisabled}
            onClick={openEditor}
            data-testid={suffixTestId('edit', props)}
          />
          <Button
            variant="dangerLink"
            leftIcon="action/delete"
            title={i18n.t('entity.watermark.composition.actions.removeGraphic')}
            isDisabled={props.isDisabled}
            onClick={props.onRemove}
            data-testid={suffixTestId('remove', props)}
          />
        </ButtonGroup>
      </Show>
    </VStack>
  );
}
